import styled, { css } from 'styled-components';

import { black, border1, formColor1, gray, green } from 'utils/style/colors';
import { scaleUp, fadeIn, rotate } from 'utils/style/animations';
import Icon from 'components/Icon';

interface IInputProps {
  color?: string;
  backgroundColor?: string;
  hasIcon?: boolean;
  loading?: boolean;
}

const scaleUpAnimation = css`
  ${scaleUp} 0.2s ease;
`;

const fadeInAnimation = css`
  ${fadeIn} 0.3s ease;
`;

const spinAnimation = css`
  ${rotate} 1s linear infinite;
`;

export const InputIcon = styled(Icon)`
  color: #aaa;
  padding: 1rem;
  position: absolute;
  transition: 0.3s;
  left: ${(props) => (props.theme.direction === 'rtl' ? '0' : undefined)};
  right: ${(props) => (props.theme.direction === 'ltr' ? '0' : undefined)};
`;

export const InputWrapper = styled('span')`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
`;

export const InputLabelTextWrapper = styled.div`
  & > label {
    font-size: 1rem;
    color: ${black};
    font-weight: bold;
    padding-inline-start: 0.25rem;
  }
`;

export const InputTextTag = styled('input')<IInputProps>`
  &[type='text'] {
    margin: 0.5rem 0 !important;
    padding: 0.1rem 0.5rem !important;
    min-height: 30px !important;
    outline: none !important;
    transition: all 0.3s !important;
    border: ${border1} !important;
    box-shadow: none !important;
    border-radius: 10px !important;
    /* background-color: var(--exact-white) !important; */
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center !important;
    height: auto !important;
    color: ${black} !important;
    background: var(--input-background) !important;
    border: none !important;
  }

  &[type='text']:hover {
  }
`;

export const InputTag = styled('input')<IInputProps>`
  &[type='number'],
  &[type='email'],
  &[type='tel'],
  &[type='password'],
  &[type='search'] {
    border: ${border1} !important;
    border-radius: 10px !important;
    padding: ${({ hasIcon }) =>
      (hasIcon ? '0 3rem 0 0.5rem' : '0 0.5rem') + ' !important'};
    position: relative;
    background-color: ${({ backgroundColor }) =>
      (backgroundColor || 'var(--input-background)') + '!important'};
    margin: 0.5rem 0 !important;
    box-sizing: border-box !important;
    color: ${black} !important;
  }
  &[type='text']:hover,
  &[type='number']:hover,
  &[type='email']:hover,
  &[type='tel']:hover,
  &[type='password']:hover {
    border-color: ${black} !important;
  }
  &[type='text']:disabled,
  &[type='number']:disabled {
    color: #a2aeb6;
    // background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAP0lEQVQoU43M2QkAIAwEUVPI9l/YFqJEUDxyzfdjpBUi2SVzigBICBfSmQtP5MIXmdBCH/TQBSO0YYYmrCCFA4f8J7ORXNL2AAAAAElFTkSuQmCC)
    //   repeat;
    pointer-events: none;
    & > * {
      opacity: 0.5;
    }
  }

  &[type='email']:disabled,
  &[type='tel']:disabled,
  &[type='password']:disabled {
    background: transparent !important;
  }
  &[type='checkbox'] {
    position: relative !important;
    display: none;
  }
  &[type='checkbox'] + label {
    color: ${(props) =>
      props.color ? props.color : 'var(--primary-form-color)'};
    position: relative;
    padding-inline-end: 1rem;
    display: inline;
    cursor: pointer;
    user-select: none;
    padding-top: 3px;
  }
  &[type='checkbox'] + label:before {
    content: '';
    display: inline-block;
    position: relative;
    margin-inline-end: 0.75rem;
    vertical-align: text-top;
    width: 21px !important;
    height: 21px !important;
    background: var(--exact-white);
    border: 1px solid ${(props) => (props.color ? props.color : formColor1)};
    border-radius: 0.35rem;
    transition: 0.3s all ease;
    margin-top: -2px;
  }
  &[type='text']:focus,
  &[type='number']:focus,
  &[type='email']:focus,
  &[type='tel']:focus,
  &[type='password']:focus {
    border-color: ${(props) => (props.color ? props.color : black)} !important;
    box-shadow: 0 0 0 2px ${gray} !important;
    + ${InputIcon} {
      color: ${black} !important;
    }
  }
  &[type='checkbox']:hover + label:before {
    border-color: ${(props) => (props.color ? props.color : black)} !important;
    box-shadow: 0 0 0 2px ${gray} !important;
  }
  &[type='checkbox']:focus + label:before {
    box-shadow: 0 2px 8px ${gray};
  }
  &[type='checkbox']:checked + label:before {
    position: relative;
    transform-origin: center;
    animation: ${scaleUpAnimation};
    background: ${(props) => (props.color ? props.color : formColor1)};
    border-color: ${(props) => (props.color ? props.color : formColor1)};
    /* disable materialize styles */
    transform: none;
    right: 0;
    top: 0;
  }
  &[type='checkbox']:checked + label:after {
    transform-origin: center;
    animation: ${(props) => (props.loading ? spinAnimation : fadeInAnimation)};
    content: '';
    display: block;
    position: absolute;
    ${(props) => css`
      ${props.theme.direction === 'rtl' ? 'right: 0.6rem' : 'left: 0.6rem'}
    `}
    //right: 3.25rem;
    top: ${(props) => (props.loading ? '7px' : '0.27em')};
    width: ${(props) => (props.loading ? '7px' : '0.4em')};
    height: ${(props) => (props.loading ? '7px' : '0.74em')};
    border: solid white;
    border-width: ${(props) => (props.loading ? '2px' : '0 2px 2px 0')};
    transform: rotate(45deg);
  }
  &[type='checkbox']:not(:checked) + label:after {
    content: '';
    ${({ loading }) =>
      loading &&
      css`
        transform-origin: center;
        animation: ${spinAnimation} !important;
        display: block !important;
        position: absolute !important;
        right: 7px !important;
        top: 7px !important;
        width: 7px !important;
        height: 7px !important;
        border: solid ${black} !important;
        border-width: 2px !important;
      `}
  }
  &[type='checkbox']:disabled + label {
    color: #b8b8b8;
    cursor: auto;
  }
  &[type='checkbox']:disabled + label:before {
    background: silver;
    border-color: silver;
  }
  &::placeholder {
    font-weight: bolder;
  }

  &[type='radio'] + label:before,
  &[type='checkbox'] + label:before {
    right: ${(props) => props.theme.direction === 'rtl' && '0rem !important'};
    left: ${(props) => props.theme.direction === 'ltr' && '0rem !important'};
  }
  &[type='radio'] + label:before,
  &[type='radio'] + label:after {
    content: '';
    position: absolute;
    right: ${(props) => props.theme.direction === 'rtl' && '0rem !important'};
    left: ${(props) => props.theme.direction === 'ltr' && '0rem !important'};
    top: 0;
  }
`;

export const InputWrapperTag = styled('div')<IInputProps>`
  * > input {
    &[type='text'],
    &[type='number'],
    &[type='email'],
    &[type='tel'],
    &[type='password'] {
      border: ${border1} !important;
      border-radius: 10px !important;
      padding: ${({ hasIcon }) =>
        (hasIcon ? '0 3rem 0 0.5rem' : '0 0.5rem') + ' !important'};
      position: relative;
      background-color: ${({ backgroundColor }) =>
        (backgroundColor || 'var(--input-background)') + '!important'};
      margin: 0.5rem 0 !important;
      box-sizing: border-box !important;
    }
    &[type='text']:hover,
    &[type='number']:hover,
    &[type='email']:hover,
    &[type='tel']:hover,
    &[type='password']:hover {
      border-color: ${black} !important;
    }
    &[type='text']:disabled,
    &[type='number']:disabled {
      color: #a2aeb6;
      // background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAP0lEQVQoU43M2QkAIAwEUVPI9l/YFqJEUDxyzfdjpBUi2SVzigBICBfSmQtP5MIXmdBCH/TQBSO0YYYmrCCFA4f8J7ORXNL2AAAAAElFTkSuQmCC)
      //   repeat;
      pointer-events: none;
      & > * {
        opacity: 0.5;
      }
    }

    &[type='email']:disabled,
    &[type='tel']:disabled,
    &[type='password']:disabled {
      background: transparent !important;
    }
    &[type='checkbox'] {
      position: relative !important;
      display: none;
    }
    &[type='checkbox'] + label {
      color: ${(props) => (props.color ? props.color : '#888888')};
      position: relative;
      padding-inline-start: 1rem;
      cursor: pointer;
      user-select: none;
    }
    &[type='checkbox'] + label:before {
      content: '';
      display: inline-block;
      vertical-align: text-top;
      width: 21px !important;
      height: 21px !important;
      background: var(--exact-white);
      border: 1px solid ${(props) => (props.color ? props.color : gray)};
      border-radius: 0.35rem;
      transition: 0.3s all ease;
    }
    &[type='text']:focus,
    &[type='number']:focus,
    &[type='email']:focus,
    &[type='tel']:focus,
    &[type='password']:focus {
      border-color: ${(props) =>
        props.color ? props.color : green} !important;
      box-shadow: 0 0 0 2px ${gray} !important;
      + ${InputIcon} {
        color: ${black} !important;
      }
    }
    &[type='checkbox']:hover + label:before {
      border-color: ${(props) =>
        props.color ? props.color : black} !important;
      box-shadow: 0 0 0 2px ${gray} !important;
    }
    &[type='checkbox']:focus + label:before {
      box-shadow: 0 2px 8px ${gray};
    }
    &[type='checkbox']:checked + label:before {
      transform-origin: center;
      animation: ${scaleUpAnimation};
      background: ${(props) => (props.color ? props.color : green)};
      border-color: ${(props) => (props.color ? props.color : green)};
      /* disable materialize styles */
      transform: none;
      right: 0;
      top: 0;
    }
    &[type='checkbox']:checked + label:after {
      transform-origin: center;
      animation: ${(props) =>
        props.loading ? spinAnimation : fadeInAnimation};
      content: '';
      display: block;
      position: absolute;
      right: 7px;
      top: ${(props) => (props.loading ? '7px' : '0.27em')};
      width: ${(props) => (props.loading ? '7px' : '0.4em')};
      height: ${(props) => (props.loading ? '7px' : '0.74em')};
      border: solid var(--exact-white);
      border-width: ${(props) => (props.loading ? '2px' : '0 2px 2px 0')};
      transform: rotate(45deg);
    }
    &[type='checkbox']:not(:checked) + label:after {
      content: '';
      ${({ loading }) =>
        loading &&
        css`
          transform-origin: center;
          animation: ${spinAnimation} !important;
          display: block !important;
          position: absolute !important;
          right: 7px !important;
          top: 7px !important;
          width: 7px !important;
          height: 7px !important;
          border: solid ${black} !important;
          border-width: 2px !important;
        `}
    }
    &[type='checkbox']:disabled + label {
      color: #b8b8b8;
      cursor: auto;
    }
    &[type='checkbox']:disabled + label:before {
      background: silver;
      border-color: silver;
    }
    &::placeholder {
      font-weight: bolder;
    }
  }
`;
